import {
	companyDocumentArchiveRoleName,
	companyImportRoleName,
	companyLimitedRoleName,
	companyNormalRoleName,
	getPresentationName,
} from "../../../CompanyUserRoles.js";
import accessRightFactory from "./accessRightFactory.js";
import Model from "../../../Model.js";

/**
 * TODO: write documentation
 */
export default class FilePermissions extends Model {
	constructor({
		accessRights,
		canMove,
		canRename,
		canDelete,
		canEditPermissions,
		inheritAccess,
	}) {
		super(...arguments);

		this._accessRights = accessRights;
		this._canMove = canMove;
		this._canRename = canRename;
		this._canDelete = canDelete;
		this._canEditPermissions = canEditPermissions;
		this._inheritAccess = inheritAccess;
	}

	canMove() {
		return this._canMove;
	}

	canRename() {
		return this._canRename;
	}

	canDelete() {
		return this._canDelete;
	}

	canEditPermissions() {
		return this._canEditPermissions;
	}

	allowsAccessToCompanyUser(user, company) {
		const role = user.getCompanyRole(company);
		if (role.isAdminRole()) {
			return true;
		}

		const accessRight = this.getAccessRightForRoleName(
			role.getTypeOfRole(),
		);
		return accessRight.allowsRead();
	}

	isInheritingAccessRights() {
		return this._inheritAccess;
	}

	setInheritAccess(newState) {
		this._inheritAccess = newState;
	}

	getAccessRightForRoleName(roleName) {
		return this._accessRights[roleName];
	}

	setAccessRightForRoleName(roleName, accessRight) {
		this._accessRights[roleName] = accessRight;
	}

	_serializedProperties() {
		const data = super._serializedProperties();

		data.inheritAccess = this._inheritAccess;

		if (!this._inheritAccess) {
			this.constructor.allRoleAccessNames().forEach((roleName) => {
				data[this.constructor._jsonFieldNameRoleName(roleName)] =
					this.getAccessRightForRoleName(roleName);
			});
		}

		return data;
	}

	static transformMaterializationData(data) {
		data.canMove = data.canBeMovedByCurrentUser;
		data.canRename = data.canBeRenamedByCurrentUser;
		data.canDelete = data.canBeDeletedByCurrentUser;
		data.canEditPermissions = data.canHaveAccessRightsChangedByCurrentUser;

		data.accessRights = {};

		this.allRoleAccessNames().forEach((roleName) => {
			data.accessRights[roleName] = this._accessRightForRoleName(
				roleName,
				data,
			);
		});

		data.accessRights[companyImportRoleName] =
			data.accessRights[companyNormalRoleName];
	}

	static getRoleLabelForRoleName(roleName) {
		return this._getRoleAccess(roleName).label;
	}

	static allRoleAccessNames() {
		return Object.keys(this._allRolesAccess());
	}

	static allAccessRights() {
		return accessRightFactory.allClasses().map((klass) => klass.instance());
	}

	static _accessRightForRoleName(roleName, data) {
		const accessRightName = data[this._jsonFieldNameRoleName(roleName)];

		if (!accessRightName) {
			return accessRightFactory.noAccessRight();
		}

		return accessRightFactory.materialize(accessRightName);
	}

	static _jsonFieldNameRoleName(roleName) {
		return this._getRoleAccess(roleName).jsonField;
	}

	static _getRoleAccess(roleName) {
		const role = this._allRolesAccess()[roleName];

		if (!role) {
			throw new Error(`Unknown role name '${roleName}'`);
		}

		return role;
	}

	static _allRolesAccess() {
		const result = {
			[companyNormalRoleName]: {
				jsonField: "normalRoleAccess",
			},
			[companyLimitedRoleName]: {
				jsonField: "limitedRoleAccess",
			},
			[companyDocumentArchiveRoleName]: {
				jsonField: "documentArchiveRoleAccess",
			},
		};

		for (const roleAccess of Object.keys(result)) {
			const label = getPresentationName(roleAccess);

			result[roleAccess].label = label;
		}

		return result;
	}
}
